import { useParams } from '@dreamstack/feature-components'
import { useFormatNumber } from '@dreamstack/i18n'
import mapValues from 'lodash/mapValues'
import type { FunctionComponent } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import tw from 'twin.macro'
import { BuyToLetGraph } from '../BuyToLetGraph'
import { BTLCalcBlock1 } from './BTLCalcBlock1'
import { BTLCalcBlock2 } from './BTLCalcBlock2'
import { BTLCalcBlock3 } from './BTLCalcBlock3'
import { BTLCalcBlock4 } from './BTLCalcBlock4'
import type { BTLCalcResultsData } from './BTLCalcUtil'
import {
  formatOptionsDecimal1,
  formatOptionsDecimal2,
  useGetChartData,
  useGetNetZeroYear,
  useGetNetZeroYearHF,
  useGetTableData,
} from './BTLCalcUtil'

type BTLCalcResultsPageParams<T> = {
  priceValue: T
  rentValue: T
  notaryFees: T
  putDownValue: T
  agentFees: T
  propertyTax: T
  interestRate: T
  appreciateValue: T
  rentalIncrease: T
  repaymentRate: T
  maintenanceValue: T
  rent: T
}

const HorizontalLineTop = tw.div`border-t-2 w-full mb-px-24 border-color[rgba(43, 45, 54, 0.2)]`

export const BTLCalcResultsPage: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const urlParams = useParams() as BTLCalcResultsPageParams<string>
  const format = useFormatNumber(formatOptionsDecimal1)
  const format2 = useFormatNumber(formatOptionsDecimal2)

  const urlParamsParsed: BTLCalcResultsPageParams<number> = mapValues(
    urlParams,
    (param) => {
      return parseFloat(param)
    }
  )

  const {
    priceValue,
    rentValue,
    putDownValue,
    propertyTax,
    notaryFees,
    agentFees,
    interestRate,
    appreciateValue,
    repaymentRate,
    maintenanceValue,
  } = urlParamsParsed

  const rentalReturn = (100 * rentValue * 12) / priceValue

  const allFees = notaryFees + agentFees + propertyTax
  const purchaseCosts = (priceValue * allFees) / 100
  const loanSize = priceValue + purchaseCosts - putDownValue
  const mortgagePayment = (loanSize * (interestRate + repaymentRate)) / 100

  const minimumDownpayment = (priceValue * (30 + allFees)) / 100
  const maintenanceCosts = (100 * 12 * maintenanceValue) / priceValue

  const profitabilityPart1 = rentalReturn - maintenanceCosts - interestRate
  const profitabilityPart2 = appreciateValue
  const profitabilityPart3 = -allFees / 15
  const profitability =
    profitabilityPart1 + profitabilityPart2 + profitabilityPart3

  const params = {
    loanSize,
    purchaseCosts,
    mortgagePayment,
    maintenanceCosts,
  }

  const tableData = useGetTableData(
    urlParamsParsed as BTLCalcResultsData,
    params
  )
  const chartData = useGetChartData(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  const yearNetZero = useGetNetZeroYear(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  // Use Hypofriend Formular to get yearNetZero Value
  const yearNetZeroHF = useGetNetZeroYearHF(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  return (
    <div tw="flex flex-col-reverse md:(flex-row)">
      <div tw="flex-1 pr-px-48">
        <HorizontalLineTop />

        <BTLCalcBlock1
          priceValue={priceValue}
          rentValue={rentValue}
          rentalReturn={rentalReturn}
        />

        <BTLCalcBlock2
          putDownValue={putDownValue}
          propertyTax={propertyTax}
          notaryFees={notaryFees}
          agentFees={agentFees}
          interestRate={interestRate}
          repaymentRate={repaymentRate}
          purchaseCosts={purchaseCosts}
          mortgagePayment={mortgagePayment}
          loanSize={loanSize}
          minimumDownpayment={minimumDownpayment}
        />

        <BTLCalcBlock3
          {...urlParamsParsed}
          maintenanceCosts={maintenanceCosts}
        />
      </div>

      <div tw="flex flex-col justify-start flex-1 background-color[rgba(255, 255, 255, 0.5)]">
        <div tw="bg-accentroWhite-full p-px-24">
          <div tw="flex-grow-0 flex-shrink font-size[20px] font-weight[600] pb-px-12">
            Profitability
          </div>

          <div tw="flex flex-row flex-1 flex-grow-0 flex-shrink">
            <div tw="flex-1 font-size[16px] pr-px-12">
              <span tw="font-size[13px] font-weight[600]">
                Your purchase is profitable after
              </span>

              <div tw="font-size[40px] color[#73c49f] font-weight[600]">
                {format2(yearNetZeroHF)} Years
              </div>
            </div>
            <div tw="flex-1 font-size[16px] pl-px-12">
              <span tw="font-size[13px] font-weight[600]">
                Initial net annual gain of investment
              </span>
              <div tw="font-size[40px] color[#73c49f] font-weight[600]">
                {format(profitability) + '%'}
              </div>
            </div>
          </div>
          <div tw="flex-1 flex-grow-0 flex-shrink my-px-24 font-size[20px] font-weight[600]">
            Cumulative Profit / Net Worth Result of investment
          </div>

          <div>
            <div tw="height[438px] flex-auto">
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                  <div
                    style={{
                      width: width,
                      height: height,
                    }}
                  >
                    <BuyToLetGraph data={chartData} />
                  </div>
                )}
              </AutoSizer>
            </div>
          </div>
        </div>

        <BTLCalcBlock4 tableData={tableData} {...urlParamsParsed} />
      </div>
    </div>
  )
}
